import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardHeader, Button, CardBody, Collapse } from "reactstrap";
import { ClearUpdateInstanceSettingsPurgeAfterDays, GetInstanceSettingsPergeAfterDays, UpdateInstanceSettingsPurgeAfterDays } from "../../../store/InstanceConfigAction";
import { useDispatch, useSelector } from "react-redux";
import { CoreInstanceSettingsPurgeAfterDays } from "../../../models/CoreInstanceSettings";
import { EmptyRequestState, RequestState } from "../../../models/RequestState";
import { RootState } from "../../../store/store";
import StatusCode from "../../../util/StatusCode";
import Loading from "../../misc/Loading";
import SaveStatus from "../../misc/SaveStatus";
import IntInput from "../../misc/IntInput";
import { useTranslation } from "react-i18next";

interface PurgeAfterDaysProps {
    hasEditAccess: boolean
}

const PurgeAfterDays = ({ hasEditAccess }: PurgeAfterDaysProps) => {
    const { t } = useTranslation(["configuration", "common"]);
    const dispatch = useDispatch();
    const [showPurgeAfterDays, setShowPurgeAfterDays] = useState<boolean>(true);
    const [purgeAfterDaysState, setPurgeAfterDaysState] = useState<CoreInstanceSettingsPurgeAfterDays | undefined>(undefined);

    const purgeAfterDays = useSelector<RootState, RequestState<CoreInstanceSettingsPurgeAfterDays>>(state => state.instanceSettings.purgeAfterDays);
    const updatePurgeAfterDays = useSelector<RootState, EmptyRequestState>(state => state.instanceSettings.updatePurgeAfterDays);

    useEffect(() => {
        GetInstanceSettingsPergeAfterDays()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (purgeAfterDays.code === StatusCode.COMPLETE) {
            setPurgeAfterDaysState(purgeAfterDays.data);
        }
    }, [purgeAfterDays]);

    useEffect(() => {
        if (updatePurgeAfterDays.code === StatusCode.COMPLETE) {
            setTimeout(() => {
                ClearUpdateInstanceSettingsPurgeAfterDays()(dispatch);
            }, 1500);
        }

        if (updatePurgeAfterDays.code === StatusCode.ERROR) {
            setTimeout(() => {
                ClearUpdateInstanceSettingsPurgeAfterDays()(dispatch);
                GetInstanceSettingsPergeAfterDays()(dispatch);
            }, 1500);
        }
    }, [dispatch, updatePurgeAfterDays.code]);

    const validationErrors = useMemo(() => {
        const errors: string[] = [];

        if (!purgeAfterDaysState) {
            return errors;
        }

        if (purgeAfterDaysState.purgeAfterDays < 30 || purgeAfterDaysState.purgeAfterDays > 365) {
            errors.push("purgeAfterDaysRange");
        }

        return errors;
    }, [purgeAfterDaysState]);

    const hasUpdates = useMemo(() => {
        if (!purgeAfterDaysState || purgeAfterDays.code !== StatusCode.COMPLETE) {
            return false;
        }

        return purgeAfterDaysState.purgeAfterDays !== purgeAfterDays.data.purgeAfterDays;
    }, [purgeAfterDaysState, purgeAfterDays]);

    const saveUpdates = () => {
        if (!purgeAfterDaysState || !hasUpdates || validationErrors.length > 0) {
            return;
        }

        UpdateInstanceSettingsPurgeAfterDays(purgeAfterDaysState)(dispatch);
    };

    const handlePurgeAfterDaysChange = useCallback((_: string, value: number) => {
        setPurgeAfterDaysState({ purgeAfterDays: value });
    }, []);

    const disableSave = !hasUpdates || !hasEditAccess || validationErrors.length > 0 || updatePurgeAfterDays.code === StatusCode.PENDING;

    return (
        <Card className="mt-3">
            <CardHeader tag="h5">
                <Button outline color="secondary" style={{ width: "30px", float: "right", lineHeight: "0.8", padding: "0.5rem" }} onClick={() => setShowPurgeAfterDays(current => !current)}>{showPurgeAfterDays ? "-" : "+"}</Button>
                {t("instanceSettings.purgeDataSection", { ns: "configuration" })}
            </CardHeader>
            <Collapse isOpen={showPurgeAfterDays}>
                <CardBody>
                    {purgeAfterDays.code === StatusCode.PENDING
                        ? <Loading />
                        : purgeAfterDays.code === StatusCode.COMPLETE ?
                            <>
                                <IntInput
                                    name="purgeAfterDays"
                                    title={t("instanceSettings.purgeAfterDays", { ns: "configuration" })}
                                    updateValue={handlePurgeAfterDaysChange}
                                    value={purgeAfterDaysState?.purgeAfterDays ?? 0}
                                    disabled={!hasEditAccess}
                                    min={30}
                                    max={365}
                                    invalid={validationErrors.indexOf("purgeAfterDaysRange") !== -1}
                                    invalidFeedback={t("instanceSettings.purgeAfterDaysInvalidRange", { ns: "configuration" })}
                                />

                                <div className="edit_module">
                                    <Button color="primary" disabled={disableSave} onClick={saveUpdates}>
                                        {t("action.save", { ns: "common" })}
                                    </Button>
                                    <div style={{ marginLeft: "1rem", display: "inline-block" }}>
                                        <SaveStatus
                                            saveStatus={updatePurgeAfterDays.code}
                                            successMsg={t("moduleView.saved", { ns: "configuration" })}
                                            errorMsg={t("error.somethingWentWrong", { ns: "common" })}
                                        />
                                    </div>
                                </div>
                            </>
                            : null}
                </CardBody>
            </Collapse>
        </Card>
    )
}

export default PurgeAfterDays;
